import identity from 'lodash-es/identity';
import isEqual from 'lodash-es/isEqual';
import memoizeOne from 'memoize-one';

import { captureExceptionWithTags } from 'src/utils/sentry';
import { atlassianStore as store } from 'src/utils/store';

import type { RecentSearch } from '../types/recent-search';

export const MAX_RECENT_SEARCHES = 50;
export const MAX_AGE_MS = 4 * 7 * 24 * 60 * 60 * 1000; // 4 weeks

// subscribe and emitChange implement the external store contract required by
// React's useSyncExternalStore. Multiple hook instances (e.g. the nav dialog
// and the search route) subscribe to the same listener set. When addRecentSearch
// writes to localStorage, it calls emitChange to notify all subscribers, which
// triggers React to re-read the snapshot via getRecentSearches.
type Listener = () => void;

const listeners = new Set<Listener>();

function emitChange() {
  listeners.forEach(listener => listener());
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getStorageKey(workspaceSlug: string): string {
  return `bb.workspace.${workspaceSlug}.recentSearches`;
}

// useSyncExternalStore requires getSnapshot to return a referentially stable
// value when the data hasn't changed, otherwise it causes an infinite loop.
// We use memoizeOne with deep equality: readFromStorage is called on every
// getRecentSearches invocation, but the result is passed through a deep-equality
// memoized identity function. If the data is deeply equal to the previous call,
// the same array reference is returned, satisfying useSyncExternalStore's
// contract without manual version bookkeeping.
const EMPTY: RecentSearch[] = [];
const deepMemoized = memoizeOne(identity<RecentSearch[]>, isEqual);

function readFromStorage(workspaceSlug: string): RecentSearch[] {
  try {
    const raw: unknown = store.get(getStorageKey(workspaceSlug));

    if (!raw || !Array.isArray(raw)) {
      return [];
    }

    // Filter out invalid entries older than the max age.
    const cutoff = Date.now() - MAX_AGE_MS;

    return (raw as RecentSearch[])
      .filter(
        item =>
          item &&
          typeof item.query === 'string' &&
          typeof item.timestamp === 'number' &&
          item.timestamp >= cutoff
      )
      .sort((a, b) => b.timestamp - a.timestamp);
  } catch (e) {
    captureExceptionWithTags(e, {
      component: 'recent-search-storage',
    });
    return [];
  }
}

export function getRecentSearches(workspaceSlug: string): RecentSearch[] {
  if (!store.enabled) {
    return EMPTY;
  }

  return deepMemoized(readFromStorage(workspaceSlug));
}

export function addRecentSearch(workspaceSlug: string, query: string): void {
  const trimmed = query.trim();
  if (!trimmed || !store.enabled) {
    return;
  }

  try {
    const existing = readFromStorage(workspaceSlug);
    const withoutDuplicate = existing.filter(item => item.query !== trimmed);

    const updated: RecentSearch[] = [
      { query: trimmed, timestamp: Date.now() },
      ...withoutDuplicate,
    ].slice(0, MAX_RECENT_SEARCHES);

    store.setStrictlyNecessaryItem(getStorageKey(workspaceSlug), updated);
    emitChange();
  } catch (e) {
    captureExceptionWithTags(e, {
      component: 'recent-search-storage',
    });
  }
}
